import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import TableContainer from '@mui/material/TableContainer';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Typography } from '@components/Typography';
import Tooltip from '@components/Tooltip';
import ActionIconButton from '@components/ActionIconButton';
import DeleteIcon from '@mui/icons-material/DeleteRounded';
import EditIcon from '@mui/icons-material/EditRounded';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Table, getPaginationLabels } from '@components/Table';
import { LabelPlain } from '@components/Label';
import Avatar from '@components/Avatar';
import { useTranslation } from '@hooks';
import { PAGE_SIZE_OPTIONS } from '@constants/pagination';
import { formatDateOrEmpty } from '@helpers/dates';
import { getInitialsParts } from '@helpers/userName';
import { formatAssignedEntities } from '@helpers/formatEntitiesToString';
import { getFullName } from '@helpers/userName';
import { UserIcon } from 'assets/images/icons';

const StyledUserIcon = styled(UserIcon)({
    width: '12px !important',
    height: '12px !important',
    '& path': {
        fill: 'var(--text-tertiary)',
        fillOpacity: 1,
    },
});

const StyledTable = styled(Table)({
    '& .MuiTableBody-root .MuiTableCell-root .MuiIconButton-root:not(:first-of-type)': {
        marginLeft: 8,
    },
    '& .MuiTableBody-root .MuiTableCell-body:last-child': {
        padding: 4,
    },
});

const getTableHeader = t => {
    return [
        {
            key: 0,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.name')}
                </Typography>
            ),
        },
        {
            key: 1,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.phoneNumber')}
                </Typography>
            ),
        },
        {
            key: 2,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.email')}
                </Typography>
            ),
        },
        {
            key: 3,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.locations')}
                </Typography>
            ),
        },
        {
            key: 4,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.labels')}
                </Typography>
            ),
        },
        {
            key: 5,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.createdOn')}
                </Typography>
            ),
        },
        {
            key: 6,
            component: <Typography align="left" variant="subtitle2" />,
        },
    ];
};

const getLabels = (labels, isMobile) => {
    if (!labels?.length) {
        return '--';
    }

    const labelList = (
        <span style={{ width: 120, overflow: 'hidden', display: 'flex' }}>
            {labels.map(label => (
                <LabelPlain key={label.id} type={label.name} variant="caption">
                    {label.name}
                </LabelPlain>
            ))}
        </span>
    );

    if (isMobile) {
        return labelList;
    }

    return (
        <Tooltip placement="top" title={labels.map(l => l.name).join(', ')}>
            {labelList}
        </Tooltip>
    );
};

const getTableBody = (data, language, companies, onClickContact, t, isMobile, canManage) => {
    return data.map(contact => [
        {
            value: contact.id,
            component: (
                <Typography
                    variant="subtitle2"
                    align="left"
                    sx={{
                        width: 180,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <Avatar
                        size="small"
                        fullName={getFullName(contact?.firstName, contact?.lastName)}
                        label={getInitialsParts(contact?.firstName, contact?.lastName)}
                        icon={<StyledUserIcon />}
                    />
                    <span style={{ marginLeft: 8 }}>{getFullName(contact.firstName, contact.lastName, '-')}</span>
                </Typography>
            ),
        },
        {
            component: (
                <Typography variant="body2" align="left">
                    {contact.phoneNumber || '--'}
                </Typography>
            ),
        },
        {
            component: (
                <Typography
                    variant="body2"
                    align="left"
                    sx={{
                        color: !contact.email && 'var(--text-secondary)',
                        width: 145,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}
                >
                    {contact.email || '--'}
                </Typography>
            ),
        },
        {
            component: (
                <Typography variant="body2" align="left">
                    {formatAssignedEntities(companies, contact.companies, {
                        idKey: 'companyId',
                        labelKey: 'internalName',
                        limit: 20,
                    })}
                </Typography>
            ),
        },
        {
            component: (
                <Typography variant="body2" align="left">
                    {getLabels(contact.labels, isMobile)}
                </Typography>
            ),
        },
        {
            component: (
                <Typography variant="body2" align="left" sx={{ width: 90 }}>
                    {formatDateOrEmpty(contact.createdOn, 'dd MMM yyyy', language)}
                </Typography>
            ),
        },
        {
            component: (
                <Typography align="right" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <ActionIconButton title={t('common.view')} onClick={() => onClickContact(contact.id, 'view')}>
                        <VisibilityIcon viewBox="0 0 22 22" />
                    </ActionIconButton>
                    {canManage && (
                        <>
                            <ActionIconButton
                                title={t('common.edit')}
                                onClick={() => onClickContact(contact.id, 'edit')}
                            >
                                <EditIcon viewBox="0 0 22 22" />
                            </ActionIconButton>
                            <ActionIconButton
                                title={t('common.delete')}
                                onClick={() => onClickContact(contact.id, 'delete')}
                            >
                                <DeleteIcon viewBox="0 0 22 22" />
                            </ActionIconButton>
                        </>
                    )}
                </Typography>
            ),
        },
    ]);
};

const ContactsTable = ({
    companies,
    onClickContact,
    selectedContacts,
    setSelectedContacts,
    rowsPerPageRequests,
    pageRequests,
    onPageChange,
    onRowsPerPageChange,
    withCheckbox,
    canManage,
}) => {
    const { t } = useTranslation();
    const { data, loading, totalCount, pages } = useSelector(state => state.contacts);
    const language = useSelector(state => state.auth.user?.language);
    const isMobile = useMediaQuery('@media (max-width: 768px)');
    const columnNames = useMemo(() => getTableHeader(t), [t]);
    const rows = useMemo(
        () => getTableBody(data, language, companies, onClickContact, t, isMobile, canManage),
        [companies, data, isMobile, language, onClickContact, t, canManage],
    );

    return (
        <TableContainer>
            <StyledTable
                withHover
                data={data}
                columnNames={columnNames}
                rows={rows}
                onRowClick={contact => onClickContact(contact.id, 'view')}
                isDataLoading={loading}
                withCheckbox={withCheckbox}
                entity={t('ContactsPage.contacts')}
                selectedItems={selectedContacts}
                setSelectedItems={setSelectedContacts}
                withPagination
                paginationProps={{
                    colSpan: 8,
                    count: totalCount,
                    rowsPerPage: rowsPerPageRequests,
                    rowsPerPageOptions: PAGE_SIZE_OPTIONS,
                    page: pageRequests,
                    onPageChange,
                    onRowsPerPageChange,
                    ...getPaginationLabels(t),
                    pages,
                }}
            />
        </TableContainer>
    );
};

export default ContactsTable;
